import { toPx } from "~/utils/components";

import "./ProgressRing.css";

import type { CSSProperties } from "react";
import type { SizeProp } from "../components";

export interface ProgressRingProps {
  size?: Extract<SizeProp, "xs" | "sm" | "md" | "lg">;
  value: number;
}

export function getSizeValue(size: ProgressRingProps["size"]) {
  switch (size) {
    case "xs":
      return 20;
    case "sm":
      return 24;
    case "lg":
      return 64;
    default:
      return 32;
  }
}

export function getCustomProperties(props: ProgressRingProps) {
  const size = getSizeValue(props.size);
  const stroke = size / Math.PI / 3;
  const center = size / 2;
  const radius = center - stroke / 2;
  const progress = Math.min(props.value * 100, 100);
  const arcLength = 2 * Math.PI * radius;
  const arcOffset = arcLength * ((100 - progress) / 100);

  return {
    "--progress-ring-size": toPx(size),
    "--progress-ring-stroke-width": toPx(stroke),
    "--progress-ring-center": toPx(center),
    "--progress-ring-radius": toPx(radius),
    "--progress-ring-arc-length": toPx(arcLength),
    "--progress-ring-arc-offset": toPx(arcOffset),
  } as CSSProperties;
}

export function ProgressRing({ size, value }: ProgressRingProps) {
  const parsedValue = Math.min(value * 100, 100);
  const customProperties = getCustomProperties({ size, value });

  return (
    <svg
      className="ProgressRing"
      style={customProperties}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={parsedValue}
      aria-label={`${parsedValue}%`}
    >
      <circle className="ProgressRing__track" />
      <circle className="ProgressRing__value" />
    </svg>
  );
}
